/**
 * SamplesPanel — CMS sample selection from the submitted universe (Phase I).
 *
 * Upload the universe file, set the random sample size (the CMS minimum is
 * 10) and any targeted rows (a column plus the values to pull, e.g. specific
 * reject codes), and the platform draws the sample and snapshots every
 * selected row for the record. Testing outcomes and notes are recorded per
 * case, so the sample grid IS the fieldwork worksheet.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SAMPLE_STATUS_LABELS } from './labels.js';
import { PhiUploadNotice } from './BinderPanel.jsx';

const API = process.env.REACT_APP_API_URL;

const STATUS_PILL = {
  selected: 'bg-slate-100 text-slate-600 border-slate-200',
  tested_compliant: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  tested_noncompliant: 'bg-red-50 text-red-700 border-red-200',
  pended: 'bg-amber-50 text-amber-700 border-amber-200',
  not_tested: 'bg-slate-50 text-slate-400 border-slate-200',
};

function SampleRow({ s, auditId, onChanged }) {
  const [notes, setNotes] = useState(s.notes || '');
  const [saving, setSaving] = useState(false);

  const patch = async (body) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/samples/${s.id}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      }).then(r => r.json());
      if (r.success) onChanged();
    } catch {}
    setSaving(false);
  };

  const fields = Object.entries(s.row_ref || {}).filter(([k]) => k !== '_file_row').slice(0, 5);

  return (
    <div className="border border-slate-100 rounded-lg px-3 py-2 space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${s.selection === 'targeted' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`}>
          {s.selection === 'targeted' ? 'Targeted' : 'Random draw'}
        </span>
        {s.program_area && <span className="text-[11px] font-semibold text-slate-600">{s.program_area}</span>}
        {s.row_ref?._file_row != null && <span className="text-[10px] text-slate-400">file row {s.row_ref._file_row}</span>}
        <span className={`ml-auto text-[11px] font-semibold border rounded-full px-2 py-0.5 ${STATUS_PILL[s.status] || STATUS_PILL.selected}`}>
          {SAMPLE_STATUS_LABELS[s.status] || s.status}
        </span>
      </div>
      {s.rationale && <div className="text-[11px] text-slate-400">{s.rationale}</div>}
      {fields.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {fields.map(([k, v]) => (
            <span key={k} className="text-[10px] bg-slate-50 border border-slate-100 text-slate-600 rounded px-1.5 py-0.5">
              <span className="text-slate-400">{k}:</span> {String(v)}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-slate-400 flex-shrink-0">Outcome</span>
        <select value={s.status} disabled={saving} onChange={e => patch({ status: e.target.value })} className="text-xs border border-slate-300 rounded-lg px-2 py-1 text-slate-700">
          {Object.entries(SAMPLE_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input
          value={notes}
          onChange={e => setNotes(e.target.value)}
          onBlur={() => { if (notes !== (s.notes || '')) patch({ notes }); }}
          placeholder="Testing notes"
          className="flex-1 min-w-0 text-xs border border-slate-200 rounded-lg px-2 py-1"
        />
      </div>
    </div>
  );
}

export default function SamplesPanel({ auditId }) {
  const [samples, setSamples] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [file, setFile] = useState(null);
  const [size, setSize] = useState(10);
  const [col, setCol] = useState('');
  const [vals, setVals] = useState('');
  const [programArea, setProgramArea] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/samples`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setSamples(r.samples || []);
    } catch {}
  }, [auditId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('universe', file);
      fd.append('size', String(size || 10));
      const values = vals.split(',').map(v => v.trim()).filter(Boolean);
      if (col.trim() && values.length) fd.append('targeted', JSON.stringify({ column: col.trim(), values }));
      if (programArea.trim()) fd.append('programArea', programArea.trim());
      const res = await fetch(`${API}/api/audit/audits/${auditId}/samples/generate`, { method: 'POST', credentials: 'include', body: fd });
      const d = await res.json();
      if (!d.success) { setWarnings(d.warnings || []); throw new Error(d.message || 'The sample could not be drawn.'); }
      setWarnings(d.warnings || []);
      setFile(null);
      await load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const tested = samples.filter(s => /^tested_/.test(s.status)).length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div>
        <div className="text-sm font-semibold text-slate-800">
          Sample selection <span className="text-slate-400 font-normal">· {samples.length ? `${samples.length} case${samples.length === 1 ? '' : 's'}, ${tested} tested` : 'none drawn yet'}</span>
        </div>
        <div className="text-[11px] text-slate-400">Upload the submitted universe and the platform draws the CMS sample: at least 10 random cases, plus any targeted cases you name. Selected rows are snapshotted with member identifiers removed — names, birth dates, and Medicare numbers never persist.</div>
      </div>

      <PhiUploadNotice />

      <div className="grid sm:grid-cols-2 gap-2 bg-slate-50 border border-slate-100 rounded-lg p-3">
        <div className="sm:col-span-2 flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden" data-testid="universe-file" onChange={e => setFile(e.target.files?.[0] || null)} />
          <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
            {file ? file.name : '↑ Choose the universe file'}
          </button>
          <label className="text-xs text-slate-500 flex items-center gap-1.5">Random sample size
            <input type="number" min={1} value={size} onChange={e => setSize(e.target.value)} className="w-16 text-xs border border-slate-300 rounded-lg px-2 py-1" />
          </label>
          <input value={programArea} onChange={e => setProgramArea(e.target.value)} placeholder="Program area (e.g., CDAG)" className="text-xs border border-slate-300 rounded-lg px-2 py-1" />
        </div>
        <input value={col} onChange={e => setCol(e.target.value)} placeholder="Targeted column (optional)" className="text-xs border border-slate-300 rounded-lg px-2 py-1" />
        <input value={vals} onChange={e => setVals(e.target.value)} placeholder="Targeted values, comma-separated" className="text-xs border border-slate-300 rounded-lg px-2 py-1" />
        <div className="sm:col-span-2">
          <button type="button" onClick={generate} disabled={busy || !file} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">
            {busy ? 'Drawing the sample…' : 'Generate sample'}
          </button>
        </div>
      </div>

      {err && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}
      {warnings.length > 0 && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-0.5">
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}

      {samples.length > 0 && <div className="space-y-2">{samples.map(s => <SampleRow key={s.id} s={s} auditId={auditId} onChanged={load} />)}</div>}
    </div>
  );
}
